import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCoffee, FiClock, FiHeart, FiLeaf, FiArrowRight } = FiIcons;

function Restaurant() {
  const menuHighlights = [
    {
      category: 'Traditional Rice & Curry',
      image: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      description: 'Authentic Sri Lankan rice and curry with 6-8 vegetable sides, slow-cooked in traditional clay pots.',
      price: 'From LKR 1,200',
      badges: ['Halal', 'Vegan Options']
    },
    {
      category: 'Fresh Kottu',
      image: 'https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      description: 'Freshly chopped roti stir-fried with vegetables and your choice of chicken, beef, or vegetables.',
      price: 'From LKR 800',
      badges: ['Made to Order', 'Customizable']
    },
    {
      category: 'Packer\'s Breakfast',
      image: 'https://images.unsplash.com/photo-1551782450-17144efb9c50?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      description: 'Pre-ordered packed breakfast for early morning treks. Sandwiches, boiled eggs, fruit, and juice.',
      price: 'LKR 600',
      badges: ['Pre-Order', 'Trek Ready']
    }
  ];

  const features = [
    {
      icon: FiClock,
      title: 'Early Bird Service',
      description: 'Kitchen opens at 5:30 AM for trek departures'
    },
    {
      icon: FiHeart,
      title: 'Halal Certified',
      description: 'Certified halal kitchen with separate preparation areas'
    },
    {
      icon: FiLeaf,
      title: 'Fresh Ingredients',
      description: 'Locally sourced vegetables and spices from Kolonna markets'
    }
  ];

  return (
    <section id="restaurant" className="py-20 bg-gradient-to-b from-slate-900 to-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
            Authentic Sri Lankan
            <span className="gradient-text block">Cuisine</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Savor traditional flavors prepared with love in our halal-certified kitchen. 
            From hearty rice and curry to fresh kottu, fuel your adventure with authentic taste.
          </p>
        </motion.div>

        {/* Menu Highlights */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {menuHighlights.map((item, index) => (
            <motion.div
              key={item.category}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2, duration: 0.8 }}
              whileHover={{ scale: 1.03, transition: { duration: 0.3 } }}
              className="glass-morphism rounded-2xl overflow-hidden hover:glass-morphism-dark transition-all duration-300"
            >
              {/* Food Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.category}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                />
                <div className="absolute top-4 right-4">
                  <span className="bg-emerald-500 px-3 py-1 rounded-full text-sm font-bold text-white">
                    {item.price}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-serif font-bold text-white mb-3">{item.category}</h3>
                <p className="text-gray-300 mb-4 text-sm leading-relaxed">{item.description}</p>
                
                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                  {item.badges.map((badge) => (
                    <span
                      key={badge}
                      className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs font-medium"
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid md:grid-cols-3 gap-8 mb-16"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="text-center space-y-4"
            >
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                <SafeIcon icon={feature.icon} className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-semibold text-white">{feature.title}</h3>
              <p className="text-gray-300 text-sm leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* WhatsApp Ordering CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="glass-morphism rounded-2xl p-8 text-center"
        >
          <SafeIcon icon={FiCoffee} className="w-12 h-12 text-emerald-400 mx-auto mb-6" />
          <h3 className="text-2xl font-serif font-bold text-white mb-4">
            Order Ahead with WhatsApp
          </h3>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Skip the wait and pre-order your meals via WhatsApp. Perfect for early morning departures 
            or when you want your food ready upon arrival.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/menu"
              className="liquid-button bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <span>View Full Menu</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
            <a
              href="https://wa.me/94771234567"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300"
            >
              Order via WhatsApp
            </a>
          </div>

          <div className="mt-6 text-sm text-gray-400">
            <p>Kitchen Hours: 5:30 AM - 10:00 PM • Pre-orders accepted until 9:00 PM</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Restaurant;